import { Injectable, OnDestroy, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subscription } from 'rxjs';
import { map, startWith, delay } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { Images, Includes, Product } from '../classes/product';
import { ProductModel } from '@app/core/models/product/product.model';
import { VendorState } from 'src/@allxs/store/state/vendor.state';
import { VendorStoreModel } from '@app/core/models/vendor/vendor-store.model';
import { Select } from '@ngxs/store';
import { cloneDeep } from 'lodash-es';
import { Store as nxgsStore } from "@ngxs/store";
import { ReleaseInventoryAction, ReleaseInventoryBasicAction } from 'src/@allxs/store/actions/product.actions';


const state = {
  products: JSON.parse(localStorage['products'] || '[]'),
  wishlist: JSON.parse(localStorage['wishlistItems'] || '[]'),
  compare: JSON.parse(localStorage['compareItems'] || '[]'),
  cart: JSON.parse(localStorage['cartItems'] || '[]')
}

@Injectable({
  providedIn: 'root'
})
export class ProductService implements OnInit, OnDestroy {

  public Currency = { name: 'Rand', currency: 'R', price: 1 } // Default Currency
  public OpenCart: boolean = false;
  public Products;
  public ProductCatalogue;
  private subscription = new Subscription();

  @Select(VendorState.deliveryProduct) deliveryProduct$: Observable<any>;


  constructor(private http: HttpClient,
    private ngsxStore: nxgsStore,
    private toastrService: ToastrService) { }

  ngOnInit(): void {
    this.subscription.add(this.deliveryProduct$.subscribe(data => {
      if (data && data.length > 0) {
        var localData = cloneDeep(data[0]);
        var deliveryProduct = state.cart.find(x => x.id = localData.productId);
        if (!deliveryProduct) {
          var test: Product = { id: localData.productId, title: localData.productName, price: localData.sellingPrice.amount, description: localData.description, place: localData.shortDescription, images: [{ src: localData.productImages.cardImage }], productType: localData.productType, attributes: localData.attributes };
          //   test.id = data[0].productId;
          state.cart.push({
            ...test,
            quantity: 1
          })
        }
      }
    }));
  }


  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
  /*
    ---------------------------------------------
    ---------------  Product  -------------------
    ---------------------------------------------
  */

  // Product
  private get products(): Observable<Product[]> {
    this.Products = this.http.get<Product[]>('assets/data/products.json').pipe(map(data => data));
    this.Products.subscribe(next => { localStorage['products'] = JSON.stringify(next) });
    return this.Products = this.Products.pipe(startWith(JSON.parse(localStorage['products'] || '[]')));
  }

  // Get Products
  public get getProducts(): Observable<Product[]> {
    return this.products;
  }

  public get getProductCatalogue(): Observable<Product[]> {
    return this.ProductCatalogue;
  }

  // Get Products By Slug
  public getProductBySlug(slug: string): Observable<Product> {
    return this.products.pipe(map(items => {
      return items.find((item: any) => {
        return item.title.replace(/ /g, "-") === slug;
      });
    }));
  }

  createProduct(vendorProduct: ProductModel, vendorType: string): Product {
    const productImages: Images[] = [];
    let productIncludes: Includes[] = [];

    const images: Images = {
      src: vendorProduct.productImages.cardImage || ''
    };

    for (let i = 0; i < 3; i++) {
      productImages.push({ ...images });
    }

    if (vendorProduct.includes.length > 0) {
      vendorProduct.includes.forEach(includes => {
        const productInclude: Includes = {
          id: includes.productItem.productItemId,
          productId: includes.productId,
          productItemNo: includes.productItem.productItemNo,
          productName: includes.productName,
          categoryName: includes.productItem.categoryName,
          categoryTabOpen: includes.productItem.categoryTabOpen,
          categoryOptionCount: includes.productItem.categoryOptionCount,
          categoryRequired: includes.productItem.categoryRequired,
          valueType: includes.productItem.valueType,
          valueVisible: includes.productItem.valueVisible,
          value: includes.productItem.value,
          quantity: includes.productItem.quantity,
          includedQuantity: includes.productItem.includedQuantity,
          maxQuantity: includes.productItem.maxQuantity
        };

        productIncludes.push(productInclude);
      })
    }

    const product: Product = {
      id: vendorProduct.productId,
      title: vendorProduct.productName,
      price: vendorProduct.sellingPrice.amount,
      userDefinedPrice: vendorProduct.sellingPrice.userDefined,
      description: vendorProduct.description,
      place: vendorType,
      simpleProduct: vendorProduct.simpleProduct,
      ticket: vendorProduct.ticket.enabled,
      inventory: vendorProduct.inventory,
      categoryId: vendorProduct.categoryId,
      categoryName: vendorProduct.categoryName,
      unitType: vendorProduct.sellingPrice.unitType,
      currency: vendorProduct.sellingPrice.currency,
      images: productImages,
      includes: productIncludes,
      productType: vendorProduct.productType,
      attributes: vendorProduct.attributes

    };

    return product;
  }

  cleanUpDescription(description: string): string {
    let cleanedDescription = description.replace(/<[^>]*>?/gm, '');
    cleanedDescription = cleanedDescription.replace(/&[^\s]*;/g, '');

    return cleanedDescription;
  }

  getProductDetailsBySlug(vendorList, slug): Product | undefined {
    for (const specificVendor of vendorList) {
      const productDetail = specificVendor.vendorProducts.find(vendorProduct => {
        return vendorProduct.productName.replace(' ', '-') === slug;
      });

      if (productDetail) {
        const productImages: Images[] = [];
        const images: Images = {
          src: productDetail.productImages.cardImage
        };

        // Push the same 'images' object multiple times
        for (let i = 0; i < 2; i++) {
          productImages.push({ ...images });
        }

        const productDescription = this.cleanUpDescription(productDetail.description);

        const productItem: Product = {
          id: productDetail.productId,
          title: productDetail.productName,
          price: productDetail.sellingPrice.amount,
          description: productDescription,
          place: productDetail.shortDescription,
          images: productImages,
          productType: productDetail.productType,
          attributes: productDetail.attributes
        };

        return productItem;
      }
    }

    return undefined;
  }


  /*
    ---------------------------------------------
    ---------------  Wish List  -----------------
    ---------------------------------------------
  */

  // Get Wishlist Items
  public get wishlistItems(): Observable<Product[]> {
    const itemsStream = new Observable(observer => {
      observer.next(state.wishlist);
      observer.complete();
    });
    return <Observable<Product[]>>itemsStream;
  }

  // Add to Wishlist
  public addToWishlist(product): any {
    const wishlistItem = state.wishlist.find(item => item.id === product.id)
    if (!wishlistItem) {
      state.wishlist.push({
        ...product
      })
    }
    this.toastrService.success('Product has been added in wishlist.');
    localStorage.setItem("wishlistItems", JSON.stringify(state.wishlist));
    return true
  }

  // Remove Wishlist items
  public removeWishlistItem(product: Product): any {
    const index = state.wishlist.indexOf(product);
    state.wishlist.splice(index, 1);
    localStorage.setItem("wishlistItems", JSON.stringify(state.wishlist));
    return true
  }

  /*
    ---------------------------------------------
    -------------  Compare Product  -------------
    ---------------------------------------------
  */

  // Get Compare Items
  public get compareItems(): Observable<Product[]> {
    const itemsStream = new Observable(observer => {
      observer.next(state.compare);
      observer.complete();
    });
    return <Observable<Product[]>>itemsStream;
  }

  // Add to Compare
  public addToCompare(product): any {
    const compareItem = state.compare.find(item => item.id === product.id)
    if (!compareItem) {
      state.compare.push({
        ...product
      })
    }
    this.toastrService.success('Product has been added in compare.');
    localStorage.setItem("compareItems", JSON.stringify(state.compare));
    return true
  }

  // Remove Compare items
  public removeCompareItem(product: Product): any {
    const index = state.compare.indexOf(product);
    state.compare.splice(index, 1);
    localStorage.setItem("compareItems", JSON.stringify(state.compare));
    return true
  }

  /*
    ---------------------------------------------
    -----------------  Cart  --------------------
    ---------------------------------------------
  */

  // Get Cart Items
  public get cartItems(): Observable<Product[]> {
    const itemsStream = new Observable(observer => {
      observer.next(state.cart);
      observer.complete();
    });
    return <Observable<Product[]>>itemsStream;
  }

  // Add to Cart
  public addToCart(product: Product): any {
    // this.subscription.add(this.deliveryProduct$.subscribe(data => {
    //   const cartItem = state.cart.find(item => item.id === product.id);
    //   const qty = product.quantity ? product.quantity : 1;
    //   const items = cartItem ? cartItem : product;
    //   const stock = this.calculateStockCounts(items, qty);

    //   if (!stock) return false


    //   if (data && data.length > 0) {
    //     var localData = cloneDeep(data[0]);
    //     var deliveryProduct = state.cart.find(x => x.id = localData.productId);
    //     if (!deliveryProduct) {
    //       var test: Product = {id: localData.productId, title: localData.productName, price: localData.sellingPrice.amount, description: localData.description, place: localData.shortDescription, images: [{src: localData.productImages.cardImage}], productType: localData.productType, attributes: localData.attributes};
    //       //   test.id = data[0].productId;
    //       state.cart.push({
    //         ...test,
    //         quantity: 1
    //       })
    //     }
    //   }



    //   if (cartItem) {
    //     cartItem.quantity += qty
    //   } else {
    //     state.cart.push({
    //       ...product,
    //       quantity: qty
    //     })
    //   }

    //   this.OpenCart = true; // If we use cart variation modal
    //   localStorage.setItem("cartItems", JSON.stringify(state.cart));
    //   return true;
    // }));
    let cartItem = null
    if (product.simpleProduct) {
      cartItem = state.cart.find(item => item.id === product.id);
    }
    const qty = product.quantity ? product.quantity : 1;
    const items = cartItem ? cartItem : product;
    const stock = this.calculateStockCounts(items, qty);

    if (!stock) return false

    const deliveryProduct = this.ngsxStore.selectSnapshot(state => state.vendor.deliveryProduct);
    if (deliveryProduct && deliveryProduct.length > 0) {
      var cartDeliveryProduct = state.cart.find(x => x.id == deliveryProduct[0].productId);
      if (!cartDeliveryProduct) {
        var test: Product = {
          id: deliveryProduct[0].productId,
          title: deliveryProduct[0].productName,
          price: deliveryProduct[0].sellingPrice.amount,
          description: deliveryProduct[0].description,
          place: deliveryProduct[0].shortDescription,
          images: [{ src: deliveryProduct[0].productImages.cardImage }],
          productType: deliveryProduct[0].productType,
          attributes: deliveryProduct[0].attributes
        };
        state.cart.push({
          ...test,
          quantity: 1,
          dateAdded: new Date().toLocaleString()
        })
      }
    }

    if (cartItem) {
      cartItem.quantity += qty
    } else {
      state.cart.push({
        ...product,
        quantity: qty,
        dateAdded: new Date().toLocaleString()
      })
    }

    this.OpenCart = true; // If we use cart variation modal
    localStorage.setItem("cartItems", JSON.stringify(state.cart));
    return true;

  }

  // Update Cart Quantity
  public updateCartQuantity(product: Product, quantity: number): Product | boolean {
    return state.cart.find((items, index) => {
      if (items.id === product.id) {
        const qty = state.cart[index].quantity + quantity
        const stock = this.calculateStockCounts(state.cart[index], quantity)
        if (qty !== 0 && stock) {
          state.cart[index].quantity = qty
        }
        localStorage.setItem("cartItems", JSON.stringify(state.cart));
        return true
      }
    })
  }

  public updateCartPrices(product: Product, quantity: number): Product | boolean {
    return state.cart.find((items, index) => {
      if (items.id === product.id) {
        const qty = state.cart[index].quantity + quantity
        const stock = this.calculateStockCounts(state.cart[index], quantity)
        if (qty !== 0 && stock) {
          state.cart[index].quantity = qty
        }
        localStorage.setItem("cartItems", JSON.stringify(state.cart));
        return true
      }
    })
  }

  // Set Cart Item Quantity (direct update)
  public setCartItemQuantity(product: Product): boolean {
    const cartItem = state.cart.find(item => item.id === product.id && item.salesincludes === product.salesincludes);
    if (cartItem) {
      cartItem.quantity = product.quantity;
      localStorage.setItem("cartItems", JSON.stringify(state.cart));
      return true;
    }
    return false;
  }

  // Calculate Stock Counts
  public calculateStockCounts(product, quantity) {
    const qty = product.quantity + quantity
    const stock = product.stock
    if (stock < qty || stock == 0) {
      this.toastrService.error('You can not add more items than available. In stock ' + stock + ' items.');
      return false
    }
    return true
  }

  // Remove Cart items
  public removeCartItem(product: Product): any {
    // var test = this.deliveryProduct$.subscribe(data => {

    // const index = state.cart.indexOf(product);
    // state.cart.splice(index, 1);
    // localStorage.setItem("cartItems", JSON.stringify(state.cart));
    // if ( state.cart.length == 1) {
    //   if (data) {
    //     var localData = cloneDeep(data[0]);
    //     var deliveryProduct = state.cart.find(x => x.id = localData.productId);
    //     if (deliveryProduct) {
    //       const index = state.cart.indexOf(deliveryProduct);
    //       state.cart.splice(index, 1);
    //     }
    //   }
    // }
    // return true
    // });
    
    const deliveryProduct = this.ngsxStore.selectSnapshot(state => state?.vendor?.deliveryProduct);
    if(deliveryProduct.length > 0 && product.id == deliveryProduct[0].productId){
      return;
    }

    var attribs = product.attributes?.split(';');
    if (attribs && attribs.length > 0){
      attribs.forEach(attrib => {
        if (attrib){
          this.ngsxStore.dispatch(new ReleaseInventoryBasicAction(attrib, product.id));
        }
      });
    }
    
    let index = state.cart.indexOf(product);
    state.cart.splice(index, 1);
    localStorage.setItem("cartItems", JSON.stringify(state.cart));
    if (state.cart.length == 1) {
      if (deliveryProduct && deliveryProduct.length > 0) {
        var cartDeliveryProduct = state.cart.find(x => x.id == deliveryProduct[0].productId);
        if (cartDeliveryProduct) {
          index = state.cart.indexOf(cartDeliveryProduct);
          state.cart.splice(index, 1);
          localStorage.setItem("cartItems", JSON.stringify(state.cart));
        }
      }
    }
    return true
  }

  public clearCart(): void {
    state.cart = [];
    localStorage.removeItem("cartItems"); // 
  }

  // Check for expired cart items with attributes (older than 15 minutes)
  public checkForExpiredCartItems(): Product[] {
    const expiredItems: Product[] = [];
    const fifteenMinutesAgo = new Date(Date.now() - 15 * 60 * 1000);

    console.log('Checking for expired items...');
    console.log('Current time:', new Date());
    console.log('Fifteen minutes ago:', fifteenMinutesAgo);
    console.log('Cart items:', state.cart);

    state.cart.forEach(item => {
      console.log(`Item: ${item.title}, attributes: ${item.attributes}, dateAdded: ${item.dateAdded}`);
      if (item.attributes && item.dateAdded) {
        const dateAdded = new Date(item.dateAdded);
        console.log(`Item ${item.title} added at: ${dateAdded}, expired check: ${dateAdded < fifteenMinutesAgo}`);
        if (dateAdded < fifteenMinutesAgo) {
          expiredItems.push(item);
        }
      }
    });

    console.log('Expired items found:', expiredItems);
    return expiredItems;
  }

  // Remove expired items from cart
  public removeExpiredItems(expiredItems: Product[]): void {
    expiredItems.forEach(item => {
      this.removeCartItem(item);
    });
  }

  // Total amount 
  public cartTotalAmount(): Observable<number> {
    return this.cartItems.pipe(map((product: Product[]) => {
      const total = product.reduce((prev, curr: Product) => {
        let baseProduct = curr.quantity * curr.price;
        
        // Apply discount if available
        if (curr.discount) {
          const discountedPrice = curr.price - (curr.price * curr.discount / 100);
          baseProduct = curr.quantity * discountedPrice;
        }
        
        // Add salesincludes items
        if (curr.salesincludes) {
          curr.salesincludes.forEach(include => {
            if (include.items && include.items.length > 0) {
              include.items.forEach(item => {
                baseProduct += curr.quantity * item.quantity * item.value;
              });
            }
          });
        }
        
        return prev + baseProduct;
      }, 0);
      return total * this.Currency.price;
    }));
  }


  /*
    ---------------------------------------------
    ------------  Filter Product  ---------------
    ---------------------------------------------
  */

  // Get Product Filter
  public filterProducts(filter: any): Observable<Product[]> {
    return this.products.pipe(map(product =>
      product.filter((item: Product) => {
        if (!filter.length) return true
        const Tags = filter.some((prev) => { // Match Tags
          if (item.tags) {
            if (item.tags.includes(prev)) {
              return prev
            }
          }
        })
        return Tags
      })
    ));
  }

  public filterProductCatalogue(products: Product[], filter: string): Product[] {
    return products.filter(product => {
      if (!filter.length) return true; // If no filter applied, return all products
      // Check if product's category ID matches any category ID in the filter array
      return filter.includes(product.categoryName);
    });
  }

  // Sorting Filter
  public sortProducts(products: Product[], payload: string): any {

    if (payload === 'ascending') {
      return products.sort((a, b) => {
        if (a.id < b.id) {
          return -1;
        } else if (a.id > b.id) {
          return 1;
        }
        return 0;
      })
    } else if (payload === 'a-z') {
      return products.sort((a, b) => {
        if (a.title < b.title) {
          return -1;
        } else if (a.title > b.title) {
          return 1;
        }
        return 0;
      })
    } else if (payload === 'z-a') {
      return products.sort((a, b) => {
        if (a.title > b.title) {
          return -1;
        } else if (a.title < b.title) {
          return 1;
        }
        return 0;
      })
    } else if (payload === 'low') {
      return products.sort((a, b) => {
        if (a.price < b.price) {
          return -1;
        } else if (a.price > b.price) {
          return 1;
        }
        return 0;
      })
    } else if (payload === 'high') {
      return products.sort((a, b) => {
        if (a.price > b.price) {
          return -1;
        } else if (a.price < b.price) {
          return 1;
        }
        return 0;
      })
    }
  }

  /*
    ---------------------------------------------
    ------------- Product Pagination  -----------
    ---------------------------------------------
  */
  public getPager(totalItems: number, currentPage: number = 1, pageSize: number = 18) {
    // calculate total pages
    let totalPages = Math.ceil(totalItems / pageSize);

    // Paginate Range
    let paginateRange = 3;

    // ensure current page isn't out of range
    if (currentPage < 1) {
      currentPage = 1;
    } else if (currentPage > totalPages) {
      currentPage = totalPages;
    }

    let startPage: number, endPage: number;
    if (totalPages <= 5) {
      startPage = 1;
      endPage = totalPages;
    } else if (currentPage < paginateRange - 1) {
      startPage = 1;
      endPage = startPage + paginateRange - 1;
    } else {
      startPage = currentPage - 1;
      endPage = currentPage + 1;
    }

    // calculate start and end item indexes
    let startIndex = (currentPage - 1) * pageSize;
    let endIndex = Math.min(startIndex + pageSize - 1, totalItems - 1);

    // create an array of pages to ng-repeat in the pager control
    let pages = Array.from(Array((endPage + 1) - startPage).keys()).map(i => startPage + i);

    // return object with all pager properties required by the view
    return {
      totalItems: totalItems,
      currentPage: currentPage,
      pageSize: pageSize,
      totalPages: totalPages,
      startPage: startPage,
      endPage: endPage,
      startIndex: startIndex,
      endIndex: endIndex,
      pages: pages
    };
  }

}
function StoreSelect(selectedVendor: any): (target: ProductService, propertyKey: "selectedVendor$") => void {
  throw new Error('Function not implemented.');
}

